import { type FC } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { axiosPostCamelToSnake } from "@root/shared/utils/axiosPostCamelToSnake";
import axios, { type AxiosError } from "axios";
import { showAlert } from "@root/shared/utils";
import { sendErrorLog } from "@root/shared/utils/sendErrorLog";
import { Presentation } from "./Presentation";
import { type InputProps, schema } from "./schema";
import { type Props, type Question } from "./type";

export const EnqueteBeforeSuspenseOrCancellation: FC<Props> = ({
  backHref,
  action,
  questions,
  nextStepHref,
  defaultValues,
}) => {
  const methods = useForm<InputProps>({
    defaultValues: {
      question1Answer: defaultValues.question1Answer ?? null,
      question2Answer: defaultValues.question2Answer ?? "",
      question3Answer: defaultValues.question3Answer ?? null,
      question4Answer: defaultValues.question4Answer ?? [],
      question4AnswerOthersText: defaultValues.question4AnswerOthersText ?? "",
      question5Answer: defaultValues.question5Answer ?? null,
      question5SubAnswer: defaultValues.question5SubAnswer ?? [],
      question5SubAnswerOthersText: defaultValues.question5SubAnswerOthersText ?? "",
      question6Answer: defaultValues.question6Answer ?? null,
      question6SubAnswer: defaultValues.question6SubAnswer ?? [],
      question6SubAnswerOthersText: defaultValues.question6SubAnswerOthersText ?? "",
      question7Answer: defaultValues.question7Answer ?? null,
      question7SubAnswer: defaultValues.question7SubAnswer ?? [],
      question7SubAnswerOthersText: defaultValues.question7SubAnswerOthersText ?? "",
      question8Answer: defaultValues.question8Answer ?? null,
      question8SubAnswer: defaultValues.question8SubAnswer ?? [],
      question8SubAnswerOthersText: defaultValues.question8SubAnswerOthersText ?? "",
      question9Answer: defaultValues.question9Answer ?? [],
      question9AnswerOthersText: defaultValues.question9AnswerOthersText ?? "",
      question10Answer: defaultValues.question10Answer ?? null,
      question11Answer: defaultValues.question11Answer ?? "",
    },
    resolver: zodResolver(schema),
    mode: "all",
  });
  const { handleSubmit, watch, setError } = methods;
  const question5Value = watch(questions.question5.name as keyof InputProps) as string | undefined;
  const question6Value = watch(questions.question6.name as keyof InputProps) as string | undefined;
  const question7Value = watch(questions.question7.name as keyof InputProps) as string | undefined;
  const question8Value = watch(questions.question8.name as keyof InputProps) as string | undefined;

  const question4Value = watch(questions.question4.checkboxesProps?.name as keyof InputProps) as
    string[] | undefined;
  const question9Value = watch(questions.question9.checkboxesProps?.name as keyof InputProps) as
    string[] | undefined;
  const question5SubValue = watch(
    questions.question5Sub.checkboxesProps?.name as keyof InputProps,
  ) as string[] | undefined;
  const question6SubValue = watch(
    questions.question6Sub.checkboxesProps?.name as keyof InputProps,
  ) as string[] | undefined;
  const question7SubValue = watch(
    questions.question7Sub.checkboxesProps?.name as keyof InputProps,
  ) as string[] | undefined;
  const question8SubValue = watch(
    questions.question8Sub.checkboxesProps?.name as keyof InputProps,
  ) as string[] | undefined;

  // ラジオボタンで「不満」「どちらかといえば不満」が選択されているか判定
  const isDissatisfied = (items: Question[], value: string | undefined) => {
    const selected = items.find(
      (q) => String(q.radioButtonProps.value ?? "") === String(value ?? ""),
    );
    return selected?.text === "不満" || selected?.text === "どちらかといえば不満";
  };

  // チェックボックスで「その他」が選択されているか判定
  const isOtherChecked = (
    items: { text: string; checkboxProps: { value?: string | number | readonly string[] } }[],
    value: string[] | undefined,
  ) => {
    const otherValue = items.find((q) => q.text === "その他")?.checkboxProps.value;
    return typeof otherValue === "string" && Array.isArray(value) && value.includes(otherValue);
  };

  const handleOnSubmit = (data: InputProps) => {
    axiosPostCamelToSnake
      .post(action, {
        formClientFrontendClientCancelEnquete: data,
      })
      .then(() => {
        window.location.href = nextStepHref;
      })
      .catch(
        (
          error: AxiosError<{
            errors?: Record<string, string[] | string>;
          }>,
        ) => {
          if (!axios.isAxiosError(error) || !error.response) {
            sendErrorLog(new Error(error.message || "解約アンケートの送信に失敗しました。"));
            showAlert("送信に失敗しました。時間をおいて再度お試しください。");
            return;
          }
          switch (error.status) {
            case 422: {
              const serverErrors = error.response?.data?.errors;
              if (serverErrors && typeof serverErrors === "object") {
                Object.entries(serverErrors).forEach(([field, messages]) => {
                  setError(field as keyof InputProps, {
                    type: "server",
                    message: String(messages),
                  });
                });
              }
              break;
            }
            default:
              sendErrorLog(new Error(error.message || "解約アンケートの送信に失敗しました。"));
              showAlert("送信に失敗しました。時間をおいて再度お試しください。");
          }
        },
      );
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleOnSubmit)}>
        <Presentation
          backHref={backHref}
          questions={questions}
          selectedQuestionSubFlags={{
            question5: isDissatisfied(questions.question5.questions, question5Value),
            question6: isDissatisfied(questions.question6.questions, question6Value),
            question7: isDissatisfied(questions.question7.questions, question7Value),
            question8: isDissatisfied(questions.question8.questions, question8Value),
          }}
          selectedQuestionOtherFlags={{
            question4: isOtherChecked(
              questions.question4.checkboxesProps?.questions ?? [],
              question4Value,
            ),
            question9: isOtherChecked(
              questions.question9.checkboxesProps?.questions ?? [],
              question9Value,
            ),
            question5Sub: isOtherChecked(
              questions.question5Sub.checkboxesProps?.questions ?? [],
              question5SubValue,
            ),
            question6Sub: isOtherChecked(
              questions.question6Sub.checkboxesProps?.questions ?? [],
              question6SubValue,
            ),
            question7Sub: isOtherChecked(
              questions.question7Sub.checkboxesProps?.questions ?? [],
              question7SubValue,
            ),
            question8Sub: isOtherChecked(
              questions.question8Sub.checkboxesProps?.questions ?? [],
              question8SubValue,
            ),
          }}
        />
      </form>
    </FormProvider>
  );
};
